/**
 * Thumbs up/down feedback UI for the AG-UI assistant.
 *
 * Renders inline in the assistant action bar area after the last assistant
 * message. Shows a popover with an optional text input when a thumb is clicked.
 */
import { useState } from "react";
import { Link as LinkIcon, ThumbsDown, ThumbsUp, X } from "lucide-react";
import { CopyButton } from "~/components/assistant/CopyButton";
import {
  NEGATIVE_IMPACT_LABELS,
  NEGATIVE_IMPACT_OPTIONS,
  POSITIVE_IMPACT_LABELS,
  POSITIVE_IMPACT_OPTIONS,
} from "~/components/assistant/impact-options";
import type { FeedbackCreate, TurnFeedback } from "~/components/assistant/types";
import { Button } from "~/components/ui/button";
import { Label } from "~/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Textarea } from "~/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";

interface MessageFeedbackProps {
  feedback: FeedbackCreate | null;
  isSubmitting: boolean;
  onSubmit: (value: 0 | 1, text?: string | null, impact?: number | null) => Promise<void>;
  onClear?: () => Promise<void>;
}

/**
 * Read-only display of feedback submitted on a turn — shown in the shared
 * conversation viewer so admins can see at a glance what users flagged.
 *
 * The `#turn-{id}` URL form is the same one feedback nav prev/next writes,
 * and is also accepted by TurnScrollEffects on load.
 */
export function SubmittedFeedbackDisplay({
  feedback,
  turnId,
}: {
  feedback: TurnFeedback;
  turnId?: string;
}) {
  const isPositive = feedback.feedback_value === 1;
  const tone = isPositive ? "text-green-700" : "text-red-700";
  const impactLabel =
    feedback.impact != null
      ? isPositive
        ? POSITIVE_IMPACT_LABELS[feedback.impact]
        : NEGATIVE_IMPACT_LABELS[feedback.impact]
      : null;
  const feedbackUrl = turnId
    ? `${window.location.origin}${window.location.pathname}#turn-${turnId}`
    : "";

  return (
    <div className="text-xs group">
      <div className={cn("flex items-center gap-2", tone)}>
        {isPositive ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-block align-middle">
                <ThumbsUp className="size-3.5" fill="currentColor" />
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <p>User found this helpful</p>
            </TooltipContent>
          </Tooltip>
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-block align-middle">
                <ThumbsDown className="size-3.5" fill="currentColor" />
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <p>User found this not helpful</p>
            </TooltipContent>
          </Tooltip>
        )}
        <span className="font-semibold">User Feedback</span>
        {impactLabel && <span className="text-xs font-normal">&mdash; {impactLabel}</span>}
        {feedbackUrl && (
          <CopyButton
            text={feedbackUrl}
            icon={LinkIcon}
            tooltipText="Copy link to this feedback"
            variant="light"
            size="sm"
            className="opacity-0 group-hover:opacity-100 transition-opacity"
          />
        )}
      </div>
      {feedback.text && (
        <div className={cn("text-xs mt-1", tone)}>&ldquo;{feedback.text}&rdquo;</div>
      )}
    </div>
  );
}

export function MessageFeedbackInput({
  feedback,
  isSubmitting,
  onSubmit,
  onClear,
}: MessageFeedbackProps) {
  const [openPopover, setOpenPopover] = useState<"up" | "down" | null>(null);
  const [textFeedback, setTextFeedback] = useState("");
  const [impact, setImpact] = useState<number | null>(null);

  const currentValue = feedback?.feedback_value ?? null;

  const handleThumbClick = (thumbType: "up" | "down") => {
    if (openPopover === thumbType) {
      setOpenPopover(null);
      return;
    }

    const isSameDirection =
      (thumbType === "up" && currentValue === 1) || (thumbType === "down" && currentValue === 0);

    setOpenPopover(thumbType);
    setTextFeedback(isSameDirection ? feedback?.text || "" : "");
    setImpact(isSameDirection ? (feedback?.impact ?? null) : null);
  };

  const handleSubmit = async () => {
    if (isSubmitting || !openPopover) return;

    const feedbackValue = openPopover === "up" ? 1 : 0;
    await onSubmit(feedbackValue as 0 | 1, textFeedback.trim() || null, impact);
    setOpenPopover(null);
    setTextFeedback("");
    setImpact(null);
  };

  const handleClear = async () => {
    if (isSubmitting) return;
    await onClear?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
  };

  const popoverContent = (thumbType: "up" | "down") => {
    const impactOptions = thumbType === "up" ? POSITIVE_IMPACT_OPTIONS : NEGATIVE_IMPACT_OPTIONS;

    return (
      <div className="flex flex-col gap-3">
        <div className="font-medium text-sm">Feedback</div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="impact-select" className="text-xs text-gray-600">
            Impact: (optional)
          </Label>
          <Select
            value={impact != null ? String(impact) : "none"}
            onValueChange={(value) => setImpact(value === "none" ? null : Number(value))}
            disabled={isSubmitting}
          >
            <SelectTrigger id="impact-select" className="w-full">
              <SelectValue placeholder="Select impact level" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              {impactOptions.map((option) => (
                <SelectItem key={option.value} value={String(option.value)}>
                  {option.label}: {option.description}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="feedback-details" className="text-xs text-gray-600">
            Provide Details: (optional)
          </Label>
          <Textarea
            id="feedback-details"
            placeholder={
              thumbType === "up"
                ? "Tell us what you liked about this response"
                : "Tell us what you didn't like about this response"
            }
            value={textFeedback}
            onChange={(e) => setTextFeedback(e.target.value)}
            onKeyDown={handleKeyDown}
            className="min-h-[80px]"
            disabled={isSubmitting}
          />
        </div>
        <p className="text-xs text-gray-600">
          Your feedback will make this entire conversation visible to administrators.
        </p>
        <Button onClick={handleSubmit} disabled={isSubmitting} size="sm">
          Submit
        </Button>
      </div>
    );
  };

  return (
    <div className="flex items-center gap-1">
      {/* Thumbs Up */}
      <Popover
        open={openPopover === "up"}
        onOpenChange={(open) => {
          if (!open) setOpenPopover(null);
        }}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <button
                onClick={() => handleThumbClick("up")}
                disabled={isSubmitting}
                className={cn(
                  "p-1 rounded transition-colors text-muted-foreground",
                  "hover:bg-muted cursor-pointer disabled:opacity-50",
                  currentValue === 1 && "text-foreground"
                )}
                aria-label="Helpful"
                aria-pressed={currentValue === 1}
              >
                <ThumbsUp className="size-4" fill={currentValue === 1 ? "currentColor" : "none"} />
              </button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent>
            <p>Helpful</p>
          </TooltipContent>
        </Tooltip>
        <PopoverContent className="w-80" onCloseAutoFocus={(e) => e.preventDefault()}>
          {popoverContent("up")}
        </PopoverContent>
      </Popover>

      {/* Thumbs Down */}
      <Popover
        open={openPopover === "down"}
        onOpenChange={(open) => {
          if (!open) setOpenPopover(null);
        }}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <button
                onClick={() => handleThumbClick("down")}
                disabled={isSubmitting}
                className={cn(
                  "p-1 rounded transition-colors text-muted-foreground",
                  "hover:bg-muted cursor-pointer disabled:opacity-50",
                  currentValue === 0 && "text-foreground"
                )}
                aria-label="Not helpful"
                aria-pressed={currentValue === 0}
              >
                <ThumbsDown
                  className="size-4"
                  fill={currentValue === 0 ? "currentColor" : "none"}
                />
              </button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent>
            <p>Not helpful</p>
          </TooltipContent>
        </Tooltip>
        <PopoverContent className="w-80" onCloseAutoFocus={(e) => e.preventDefault()}>
          {popoverContent("down")}
        </PopoverContent>
      </Popover>

      {/* Clear button — only when feedback exists and delete is supported */}
      {feedback && onClear && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={handleClear}
              disabled={isSubmitting}
              className={cn(
                "p-1 rounded transition-colors text-muted-foreground",
                "hover:bg-muted cursor-pointer disabled:opacity-50"
              )}
              aria-label="Clear feedback"
            >
              <X className="size-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Clear feedback</p>
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}
